import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpIcon, ArrowDownIcon, ClockIcon } from '@heroicons/react/24/outline';
import { TeaserDemoProps } from '../../config/teaserContent';
import { createOptimizedTeaserDemo, useTeaserDemoState } from './baseTeaserDemo';

interface TradingActivityFeedDemoProps extends TeaserDemoProps {
  trades?: Array<{
    id: string;
    strategy: string;
    symbol: string;
    side: 'BUY' | 'SELL';
    quantity: number;
    price: number;
    pnl: number;
    timestamp: string;
    status: 'FILLED' | 'PENDING' | 'PARTIAL';
    isNew?: boolean;
  }>;
}

const TradingActivityFeedDemoComponent: React.FC<TradingActivityFeedDemoProps> = ({ 
  className, 
  aspectRatio = 'video',
  trades = [
    {
      id: '1',
      strategy: 'Elite Scalper',
      symbol: 'BTCUSDT',
      side: 'BUY',
      quantity: 0.25,
      price: 43250.50,
      pnl: 127.45,
      timestamp: '14:32:15',
      status: 'FILLED',
      isNew: true
    },
    {
      id: '2',
      strategy: 'Grid Master',
      symbol: 'ETHUSDT',
      side: 'SELL',
      quantity: 1.8,
      price: 2580.30,
      pnl: -43.20,
      timestamp: '14:31:42',
      status: 'FILLED'
    },
    {
      id: '3',
      strategy: 'Momentum Pro',
      symbol: 'SOLUSDT',
      side: 'BUY',
      quantity: 25.0,
      price: 98.45,
      pnl: 89.15,
      timestamp: '14:30:33',
      status: 'PARTIAL'
    }
  ]
}) => {
  const { containerClass } = useTeaserDemoState(aspectRatio);

  const formatCurrency = (amount: number) => {
    return amount >= 0 ? `+$${amount.toFixed(2)}` : `-$${Math.abs(amount).toFixed(2)}`;
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={`relative p-4 h-full flex flex-col justify-between ${containerClass} ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <motion.div
          animate={{ 
            boxShadow: [
              '0 0 10px rgba(34, 197, 94, 0.3)',
              '0 0 20px rgba(34, 197, 94, 0.6)',
              '0 0 10px rgba(34, 197, 94, 0.3)'
            ]
          }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          >
            📈
          </motion.div>
        </motion.div>
        <div>
          <h4 className="text-white font-semibold text-sm">Live Trading Feed</h4>
          <p className="text-gray-300 text-xs">{trades.filter(t => t.isNew).length} new executions</p>
        </div>
      </div>

      {/* Trade Feed */}
      <div className="space-y-2 flex-1 overflow-hidden">
        {trades.map((trade, index) => (
          <motion.div
            key={trade.id}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.2, duration: 0.5 }}
            className={`
              relative p-3 rounded-lg border backdrop-blur-sm
              ${trade.pnl >= 0 
                ? 'bg-green-500/20 border-green-500/30' 
                : 'bg-red-500/20 border-red-500/30'
              }
              ${trade.isNew ? 'ring-2 ring-blue-400/50' : ''}
            `}
          >
            {/* New Trade Pulse */}
            {trade.isNew && (
              <motion.div
                animate={{ scale: [1, 1.5, 1], opacity: [1, 0.3, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute -top-1 -right-1 w-3 h-3 bg-blue-400 rounded-full"
              />
            )}

            {/* Trade Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {trade.side === 'BUY' ? (
                  <ArrowUpIcon className="w-4 h-4 text-green-400" />
                ) : (
                  <ArrowDownIcon className="w-4 h-4 text-red-400" />
                )}
                <span className="text-white font-medium text-sm">{trade.symbol}</span>
                <span className={`
                  px-1.5 py-0.5 rounded text-xs font-medium
                  ${trade.side === 'BUY' ? 'bg-green-500/30 text-green-300' : 'bg-red-500/30 text-red-300'}
                `}>
                  {trade.side}
                </span>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <ClockIcon className="w-3 h-3" />
                {trade.timestamp}
              </div>
            </div>

            {/* Trade Details */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <p className="text-gray-400">Strategy</p>
                <p className="text-white font-medium">{trade.strategy}</p>
              </div>
              <div>
                <p className="text-gray-400">Quantity</p>
                <p className="text-white font-medium">{trade.quantity}</p>
              </div>
              <div>
                <p className="text-gray-400">Price</p>
                <p className="text-white font-medium">${formatPrice(trade.price)}</p>
              </div>
              <div>
                <p className="text-gray-400">P&L</p>
                <motion.p 
                  animate={{ scale: trade.isNew ? [1, 1.1, 1] : 1 }}
                  transition={{ duration: 0.5 }}
                  className={`font-bold ${trade.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}
                >
                  {formatCurrency(trade.pnl)}
                </motion.p>
              </div>
            </div>

            {/* Status Badge */}
            <div className="mt-2 flex justify-between items-center">
              <span className={`
                inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                ${trade.status === 'FILLED' ? 'bg-green-500/30 text-green-300' : 
                  trade.status === 'PARTIAL' ? 'bg-yellow-500/30 text-yellow-300' :
                  'bg-blue-500/30 text-blue-300'}
              `}>
                {trade.status}
              </span>
              
              {/* P&L Arrow */}
              <motion.div
                animate={{ x: [0, 3, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className={`w-4 h-4 ${trade.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}
              >
                {trade.pnl >= 0 ? '📈' : '📉'}
              </motion.div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Live Status */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.5 }}
        className="mt-3 text-center"
      >
        <motion.div
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex items-center justify-center gap-2 text-xs"
        >
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
            className="w-2 h-2 bg-green-400 rounded-full"
          />
          <span className="text-green-300 font-medium">Live market execution</span>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export const TradingActivityFeedDemo = createOptimizedTeaserDemo(
  TradingActivityFeedDemoComponent,
  'TradingActivityFeedDemo'
);